import { useCallback } from 'react';

import { useIntl } from 'react-intl';

import {
	type ResultClickedCallback,
	type ResultClickedCallbackParams,
} from '../../../common/constants/types';
import {
	onAdvancedSearchResultLinkClicked,
	type ResultEventAttributes,
} from '../../../common/utils/events/advanced-search-result-interacted';
import { isIntentDetectionTriggeredForExperiment } from '../../../common/utils/experiments/experiment-exports';
import { getExtensionData } from '../../../common/utils/relay/get-extension-data';
import { useAnalytics, useCommonAndPrivateAttributes } from '../../analytics';
import type { SharedEventAttributes } from '../../analytics/prepared-result-attributes';
import type { AnalyticsEventCallbacks } from '../../analytics/result-analytics-callbacks';
import { usePrimaryProduct } from '../../store/bootstrap';

export type FireResultClickedProps = ResultEventAttributes;

/**
 * @returns a callback to fire an event when the result
 * is clicked by the user.
 */

export const useFireResultClickedWithAttributes = (
	sharedEventAttributes: SharedEventAttributes,
	eventCallbacks: AnalyticsEventCallbacks,
	resultEventAttributes: ResultEventAttributes,
): ResultClickedCallback => {
	const { commonAttributes, nonPrivacySafeAttributes } = sharedEventAttributes;
	const { fireAnalyticsEvent } = eventCallbacks;
	return useCallback(
		(interactionAttributes: ResultClickedCallbackParams = {}) => {
			const analyticsPayload = onAdvancedSearchResultLinkClicked({
				resultEventAttributes,
				interactionAttributes,
				commonAttributes: {
					...commonAttributes,
					...getExtensionData(commonAttributes.isCQL),
				},
				nonPrivacySafeAttributes,
			});

			fireAnalyticsEvent(analyticsPayload);
		},
		[fireAnalyticsEvent, resultEventAttributes, commonAttributes, nonPrivacySafeAttributes],
	);
};

export const useFireResultClicked = (props: FireResultClickedProps): ResultClickedCallback => {
	const sharedEventAttributes = useCommonAndPrivateAttributes();
	const intl = useIntl();
	const { fireAnalyticsEvent } = useAnalytics();
	const [primaryProduct] = usePrimaryProduct();
	const intentDetectionTriggered = isIntentDetectionTriggeredForExperiment(
		primaryProduct,
		intl.locale,
	);
	return useFireResultClickedWithAttributes(
		sharedEventAttributes,
		{
			fireAnalyticsEvent,
		},
		{ ...props, intentDetectionTriggered },
	);
};
